'use client';

import { useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { usePaymentConfig } from '@/tenants/_shared/hooks/usePaymentConfig';
import { useDonationCurrency } from '@/tenants/_shared/hooks/useCurrencies';
import {
  type DonationStrategy,
  type DonationSessionInput,
  type ActivePaymentSession,
} from './types';

export function useTeamDonationStrategy(
  eventId: string | undefined,
  teamId: string | undefined,
): DonationStrategy {
  const {
    data: paymentConfig,
    isLoading: isPaymentConfigLoading,
    isFetching: isPaymentConfigFetching,
    isError: isPaymentConfigError,
  } = usePaymentConfig(eventId);

  const currency = useDonationCurrency(eventId);

  const createTeamSession = trpc.publicDonation.createTeamSession.useMutation();

  return useMemo(
    (): DonationStrategy => ({
      paymentConfig,
      isPaymentConfigLoading,
      isPaymentConfigFetching,
      isPaymentConfigError,
      currency,
      isFundraisingActive: paymentConfig?.isFundraisingActive ?? true,
      isSessionCreationPending: createTeamSession.isPending,
      checkoutScope: { scope: 'team' as const, eventId: eventId!, teamId: teamId! },
      createSession: (input: DonationSessionInput): Promise<ActivePaymentSession> =>
        createTeamSession.mutateAsync({
          ...input,
          eventId: eventId!,
          teamId: teamId!,
        }),
    }),
    [
      paymentConfig,
      isPaymentConfigLoading,
      isPaymentConfigFetching,
      isPaymentConfigError,
      currency,
      createTeamSession,
      eventId,
      teamId,
    ],
  );
}
